import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { 
  notificationService, 
  type Notification, 
  type CreateNotificationData, 
  type UpdateNotificationData, 
  type NotificationFilters,
  type NotificationStats
} from '@/services/notification.service'
import { useAuthStore } from './auth.store'

export const useNotificationStore = defineStore('notification', () => {
  // État
  const notifications = ref<Notification[]>([])
  const currentNotification = ref<Notification | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const pagination = ref({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })
  const filters = ref<NotificationFilters>({})
  const stats = ref<NotificationStats>({
    total: 0,
    unread: 0,
    read: 0,
    today: 0,
    byType: {
      info: 0,
      warning: 0,
      error: 0,
      success: 0
    },
    byCategory: {}
  })
  const unreadNotifications = ref<Notification[]>([])

  // Fonction utilitaire pour s'assurer que unreadNotifications est toujours un tableau
  const ensureUnreadNotificationsIsArray = () => {
    if (!Array.isArray(unreadNotifications.value)) {
      console.warn('unreadNotifications n\'est pas un tableau, réinitialisation:', unreadNotifications.value)
      unreadNotifications.value = []
    }
  }

  // Computed properties
  const unreadCount = computed(() => stats.value.unread)
  const readCount = computed(() => stats.value.read)
  const totalCount = computed(() => stats.value.total)
  const todayCount = computed(() => stats.value.today)
  const hasUnreadNotifications = computed(() => unreadCount.value > 0)

  // Actions
  const fetchNotifications = async (customFilters?: NotificationFilters) => {
    isLoading.value = true
    error.value = null
    
    try {
      const currentFilters = customFilters || filters.value
      const authStore = useAuthStore()
      
      // Ajouter l'utilisateur actuel aux filtres si pas déjà présent
      if (!currentFilters.userId && authStore.user) {
        currentFilters.userId = authStore.user._id
      }
      
      const result = await notificationService.getNotifications(currentFilters)
      console.log('result', result)
      notifications.value = result.notifications
      pagination.value.total = result.total
      pagination.value.totalPages = Math.ceil(result.total / pagination.value.limit)
      
      // Mettre à jour les filtres
      if (customFilters) {
        filters.value = { ...filters.value, ...customFilters }
      }
      
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des notifications'
      console.error('Erreur fetchNotifications:', err)
    } finally {
      isLoading.value = false
    }
  }

  const fetchNotificationById = async (id: string) => {
    isLoading.value = true
    error.value = null
    
    try {
      const notification = await notificationService.getNotificationById(id)
      currentNotification.value = notification
      return notification
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur lors du chargement de la notification'
      console.error('Erreur fetchNotificationById:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const createNotification = async (data: CreateNotificationData) => {
    isLoading.value = true
    error.value = null
    
    try {
      const authStore = useAuthStore()
      
      // Ajouter l'utilisateur actuel si pas spécifié
      if (!data.userId && authStore.user) {
        data.userId = authStore.user._id
      }
      
      const notification = await notificationService.createNotification(data)
      
      // Ajouter à la liste locale
      notifications.value.unshift(notification)
      
      // Mettre à jour les stats
      await fetchStats()
      
      return notification
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur lors de la création de la notification'
      console.error('Erreur createNotification:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const updateNotification = async (id: string, data: UpdateNotificationData) => {
    isLoading.value = true
    error.value = null
    
    try {
      const notification = await notificationService.updateNotification(id, data)
      
      // Mettre à jour dans la liste locale
      const index = notifications.value.findIndex(n => n._id === id)
      if (index !== -1) {
        notifications.value[index] = notification
      }
      
      // Mettre à jour la notification courante si c'est la même
      if (currentNotification.value?._id === id) {
        currentNotification.value = notification
      }
      
      // Mettre à jour les stats
      await fetchStats()
      
      return notification
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur lors de la mise à jour de la notification'
      console.error('Erreur updateNotification:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const deleteNotification = async (id: string) => {
    isLoading.value = true
    error.value = null
    
    try {
      await notificationService.deleteNotification(id)
      
      // Supprimer de la liste locale
      notifications.value = notifications.value.filter(n => n._id !== id)
      
      // Vider la notification courante si c'est la même
      if (currentNotification.value?._id === id) {
        currentNotification.value = null
      }
      
      // Mettre à jour les stats
      await fetchStats()
      
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur lors de la suppression de la notification'
      console.error('Erreur deleteNotification:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const markAsRead = async (id: string) => {
    isLoading.value = true
    error.value = null
    
    try {
      const notification = await notificationService.markAsRead(id)
      
      // Mettre à jour dans la liste locale
      const index = notifications.value.findIndex(n => n._id === id)
      if (index !== -1) {
        notifications.value[index] = notification
      }
      
      // Mettre à jour la notification courante si c'est la même
      if (currentNotification.value?._id === id) {
        currentNotification.value = notification
      }
      
      // Mettre à jour les stats
      await fetchStats()
      
      return notification
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur lors du marquage de la notification comme lue'
      console.error('Erreur markAsRead:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const markAllAsRead = async () => {
    isLoading.value = true
    error.value = null
    
    try {
      const authStore = useAuthStore()
      await notificationService.markAllAsRead(authStore.user?._id)
      
      // Mettre à jour toutes les notifications locales
      for (const notification of notifications.value) {
        notification.read = true
        notification.readAt = new Date().toISOString()
      }
      
      // Mettre à jour les stats
      await fetchStats()
      
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur lors du marquage de toutes les notifications comme lues'
      console.error('Erreur markAllAsRead:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const deleteAllNotifications = async () => {
    isLoading.value = true
    error.value = null
    
    try {
      const authStore = useAuthStore()
      await notificationService.deleteAllNotifications(authStore.user?._id)
      
      // Vider la liste locale
      notifications.value = []
      currentNotification.value = null
      
      // Mettre à jour les stats
      await fetchStats()
      
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur lors de la suppression de toutes les notifications'
      console.error('Erreur deleteAllNotifications:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const fetchStats = async (customFilters?: Omit<NotificationFilters, 'page' | 'limit'>) => {
    try {
      const currentFilters = customFilters || filters.value
      const authStore = useAuthStore()
      
      // Ajouter l'utilisateur actuel aux filtres si pas déjà présent
      if (!currentFilters.userId && authStore.user) {
        currentFilters.userId = authStore.user._id
      }
      
      const statsData = await notificationService.getNotificationStats(currentFilters)
      stats.value = statsData
      
    } catch (err) {
      console.error('Erreur fetchStats:', err)
    }
  }

  const fetchUnreadNotifications = async () => {
    try {
      const authStore = useAuthStore()
      
      // Vérifier si l'utilisateur est authentifié avant de faire la requête
      if (!authStore.isAuthenticated || !authStore.user?._id) {
        unreadNotifications.value = []
        ensureUnreadNotificationsIsArray()
        return []
      }
      
      const response = await notificationService.getUnreadNotifications(authStore.user._id)
      
      // S'assurer que la réponse est un tableau
      const notifications = Array.isArray(response.data) ? response.data : []
      unreadNotifications.value = notifications
      
      // Vérification supplémentaire
      ensureUnreadNotificationsIsArray()
      
      return notifications
    } catch (err) {
      console.error('Erreur fetchUnreadNotifications:', err);
      unreadNotifications.value = []
      ensureUnreadNotificationsIsArray()
      return []
    }
  }

  const searchNotifications = async (query: string, customFilters?: Omit<NotificationFilters, 'search'>) => {
    isLoading.value = true
    error.value = null
    
    try {
      const currentFilters = customFilters || filters.value
      const authStore = useAuthStore()
      
      // Ajouter l'utilisateur actuel aux filtres si pas déjà présent
      if (!currentFilters.userId && authStore.user) {
        currentFilters.userId = authStore.user._id
      }
      
      const result = await notificationService.searchNotifications(query, currentFilters)
      
      notifications.value = result.notifications
      pagination.value.total = result.total
      pagination.value.totalPages = Math.ceil(result.total / pagination.value.limit)
      
      // Mettre à jour les filtres
      filters.value = { ...filters.value, ...currentFilters, search: query }
      
      return result
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur lors de la recherche des notifications'
      console.error('Erreur searchNotifications:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const clearFilters = () => {
    filters.value = {}
  }

  const setFilters = (newFilters: NotificationFilters) => {
    filters.value = { ...filters.value, ...newFilters }
  }

  const setPagination = (page: number, limit?: number) => {
    pagination.value.page = page
    if (limit) {
      pagination.value.limit = limit
    }
  }

  const reset = () => {
    notifications.value = []
    currentNotification.value = null
    isLoading.value = false
    error.value = null
    pagination.value = {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0
    }
    filters.value = {}
    stats.value = {
      total: 0,
      unread: 0,
      read: 0,
      today: 0,
      byType: {
        info: 0,
        warning: 0,
        error: 0,
        success: 0
      },
      byCategory: {}
    }
    unreadNotifications.value = []
    ensureUnreadNotificationsIsArray()
  }

  // Actions pour les notifications en temps réel (à implémenter avec WebSocket)
  const addNotification = (notification: Notification) => {
    notifications.value.unshift(notification)
    // Mettre à jour les stats si nécessaire
    stats.value.total++
    if (!notification.read) {
      stats.value.unread++
    }
  }

  const updateNotificationLocal = (id: string, updates: Partial<Notification>) => {
    const index = notifications.value.findIndex(n => n._id === id)
    if (index !== -1) {
      notifications.value[index] = { ...notifications.value[index], ...updates }
    }
    
    if (currentNotification.value?._id === id) {
      currentNotification.value = { ...currentNotification.value, ...updates }
    }
  }

  // Fonction pour créer des notifications de test (à supprimer en production)
  const createTestNotifications = async () => {
    const testNotifications: CreateNotificationData[] = [
      {
        title: 'Nouvelle demande d\'absence',
        message: 'Jean Dupont a soumis une demande de congé pour la période du 15 au 20 janvier 2024.',
        type: 'info',
        category: 'Absences'
      },
      {
        title: 'Bulletin de paie généré',
        message: 'Le bulletin de paie pour le mois de décembre 2023 a été généré avec succès.',
        type: 'success',
        category: 'Paie'
      },
      {
        title: 'Erreur de synchronisation',
        message: 'Une erreur est survenue lors de la synchronisation des données employés.',
        type: 'error',
        category: 'Système'
      },
      {
        title: 'Mise à jour disponible',
        message: 'Une nouvelle version de l\'application est disponible. Veuillez redémarrer l\'application.',
        type: 'warning',
        category: 'Système'
      },
      {
        title: 'Nouvel employé ajouté',
        message: 'Marie Martin a été ajoutée à la base de données avec le matricule EMP-2024-001.',
        type: 'info',
        category: 'Employés'
      }
    ]

    for (const notificationData of testNotifications) {
      try {
        await createNotification(notificationData)
      } catch (error) {
        console.error('Erreur lors de la création de la notification de test:', error)
      }
    }
  }

  return {
    // État
    notifications,
    currentNotification,
    isLoading,
    error,
    pagination,
    filters,
    stats,
    unreadNotifications,
    
    // Computed
    unreadCount,
    readCount,
    totalCount,
    todayCount,
    hasUnreadNotifications,
    
    // Actions
    fetchNotifications,
    fetchNotificationById,
    createNotification,
    updateNotification,
    deleteNotification,
    markAsRead,
    markAllAsRead,
    deleteAllNotifications,
    fetchStats,
    fetchUnreadNotifications,
    searchNotifications,
    clearFilters,
    setFilters,
    setPagination,
    reset,
    addNotification,
    updateNotificationLocal,
    createTestNotifications
  }
})
